/**
 * The sales pipeline (GH-893, assignment + manual add GH-904): a prospective organization's
 * contact details, from the public marketing site's "book a demo" form into the console's own
 * queue.
 *
 *   POST  /v1/public/sales-leads                       public, unauthenticated, rate-limited
 *   GET   /v1/admin/sales-leads                         platform staff
 *   POST  /v1/admin/sales-leads                          platform staff, audited — manual add
 *   PATCH /v1/admin/sales-leads/:leadId                   platform staff, audited
 *   POST  /v1/admin/sales-leads/:leadId/send-calendar-invite  platform staff, audited
 *
 * A sales lead is not a donor and not an organization member — a new, isolated category of data
 * for this product. Nothing here is reachable from a donor-facing or organization-scoped route.
 */
import { z } from "zod";
export const SALES_LEAD_STAGES = ["new", "contacted", "demo_scheduled", "won", "lost"];
export const salesLeadStageSchema = z.enum(SALES_LEAD_STAGES);
/** What the marketing site's form sends. Field names match the site's own contract exactly. */
export const salesLeadCreateRequestSchema = z.object({
    name: z.string().trim().min(1).max(200),
    email: z.string().trim().min(3).max(320).email(),
    organizationName: z.string().trim().min(1).max(200),
    phone: z.string().trim().min(1).max(32).optional(),
    message: z.string().trim().min(1).max(2000).optional(),
    source: z.string().trim().min(1).max(100),
});
export const salesLeadCreateResponseSchema = z.object({
    id: z.string().uuid(),
    status: z.literal("received"),
});
/**
 * A staff member adding a lead directly from the console (GH-904) — not through the public
 * form, so there is no `source` field to accept (the route always records it as the console's
 * own source) and no rate limiting. A plain contact entry, same shape otherwise: staff can book
 * a real appointment for it afterward from the Calendar tab if one is needed.
 */
export const salesLeadManualCreateRequestSchema = z.object({
    name: z.string().trim().min(1).max(200),
    email: z.string().trim().min(3).max(320).email(),
    organizationName: z.string().trim().min(1).max(200),
    phone: z.string().trim().min(1).max(32).optional(),
    message: z.string().trim().min(1).max(2000).optional(),
});
/** One row in the console's queue. Staff-only; never sent to any public or donor surface. */
export const salesLeadRowSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string(),
    organizationName: z.string(),
    phone: z.string().nullable(),
    message: z.string().nullable(),
    source: z.string(),
    stage: salesLeadStageSchema,
    notes: z.string().nullable(),
    /** The platform staff member working this lead (GH-904), or null — unassigned by default. */
    assignedStaffUserId: z.string().uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const salesLeadListResponseSchema = z.object({
    leads: z.array(salesLeadRowSchema),
});
/** Filters the console's list may ask for. `stage` absent means every stage. */
export const salesLeadListQuerySchema = z.object({
    stage: salesLeadStageSchema.optional(),
});
/**
 * A staff edit. At least one field must be present — checked by the route, the same way
 * `updateDesignationSchema`'s callers refuse an empty patch, since a PATCH with nothing to change
 * answers a confusing "saved" to a confused caller.
 */
export const salesLeadUpdateRequestSchema = z.object({
    stage: salesLeadStageSchema.optional(),
    notes: z.string().trim().max(4000).nullable().optional(),
    /** Who owns this lead — a platform staff user id, or null to unassign (GH-904). */
    assignedStaffUserId: z.string().uuid().nullable().optional(),
});
/**
 * The response to a successful "send calendar invite" action (GH-904) — not the lead itself,
 * since the action changes nothing on the lead; just a confirmation of what was sent and to whom.
 */
export const sendCalendarInviteResponseSchema = z.object({
    appointmentId: z.string().uuid(),
    staffUserId: z.string().uuid(),
    deliveryStatus: z.enum(["queued", "sent", "delivered", "failed", "suppressed"]),
});
//# sourceMappingURL=sales-leads.js.map